// import { useState, useEffect } from 'react'
import { useState, useEffect } from 'react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { FileUploadCard } from '@/components/upload/file-upload-card'
import { MediaPreviewCard } from '@/components/upload/media-preview-card'
import { formatInstagramDisplay, instagramUrlFrom } from '@/utils/linkify'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu'
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip'
import { DollarSign, FileText, Hash, HelpCircle, Clapperboard, AudioLines, Mail } from 'lucide-react'
import { HashtagModal } from './hashtag-modal'
import { DescriptionTemplateModal } from './description-template-modal'
import { uploadToCloudinary } from '@/lib/uploadToCloudinary'
import { useProducerProfile } from '@/lib/producer-profile'
import { OnboardingModal } from '@/components/upload/onboarding-modal'

interface StepDetailsProps {
  data: any
  setData: React.Dispatch<React.SetStateAction<any>>
  onNext: () => void
  onPrevious: () => void
  isEditingDraft?: boolean
}

const getImageAspectRatio = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img.width / img.height)
    img.onerror = reject
    img.src = URL.createObjectURL(file)
  })
}

/* ===========================
   Helpers for contact block
   =========================== */
const HEADER_CONTACT = '📧 Contact & Links:'

// Detect the block by looking for the header at start-of-text or start-of-line (CRLF-safe)
const hasBlock = (text: string, header: string) => {
  const t = (text || '').replace(/\r\n/g, '\n')
  if (!t) return false
  if (t.startsWith(header)) return true
  return t.indexOf('\n' + header) !== -1
}

/**
 * Remove the entire contact block:
 * from the header line THROUGH the following lines,
 * stopping at the next blank line (two newlines) or end of text.
 * Works for \n and \r\n and with/without trailing newline.
 */
const removeContactBlock = (text: string) => {
  if (!text) return ''
  let t = text.replace(/\r\n/g, '\n')
  let start: number

  if (t.startsWith(HEADER_CONTACT)) {
    start = 0
  } else {
    const idx = t.indexOf('\n' + HEADER_CONTACT)
    if (idx === -1) return text // header not found; nothing to remove
    start = idx + 1 // skip the leading \n so we don't leave an empty line
  }

  // jump to end of header line
  const headerLineEnd = t.indexOf('\n', start)
  const searchFrom = headerLineEnd === -1 ? t.length : headerLineEnd + 1

  // next blank line (two consecutive \n)
  const blankIdx = t.indexOf('\n\n', searchFrom)
  const end = blankIdx === -1 ? t.length : blankIdx

  const before = t.slice(0, start)
  const after = t.slice(end)
  const cleaned = (before + after)
    .replace(/\n{3,}/g, '\n\n') // collapse >2 blank lines
    .trim()

  return cleaned
}

const appendBlock = (text: string, block: string) =>
  (text && text.trim().length) ? `${text.trim()}\n\n${block}` : block
/* =========================== */

export function StepDetails({
  data,
  setData,
  onNext,
  onPrevious,
  isEditingDraft = false,
}: StepDetailsProps) {
  const [uploadMode, setUploadMode] = useState<'audio-thumbnail' | 'video'>(
    data.audio || data.image ? 'audio-thumbnail' : 'video'
  )
  const [audioSrc, setAudioSrc] = useState('')
  const [videoSrc, setVideoSrc] = useState('')
  const [imageSrc, setImageSrc] = useState('')
  const [errors, setErrors] = useState<{ title?: string; description?: string; media?: string }>({})
  const [isHashtagModalOpen, setIsHashtagModalOpen] = useState(false)
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false)
  const [aspectRatioWarning, setAspectRatioWarning] = useState<string | null>(null)
  const [localContactReady, setLocalContactReady] = useState(false)

  // Quick inserts state
  const [activeInserts, setActiveInserts] = useState<Set<string>>(new Set())
  const [selectedLicense, setSelectedLicense] = useState<'nonprofit' | 'commercial' | null>(null)

  // Onboarding control (for contact gating)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [forceStep, setForceStep] = useState<1 | 2 | 3 | undefined>(undefined)
  const [requireContactOnThisOpen, setRequireContactOnThisOpen] = useState(false)

  // Profile (must expose hasContact + loading in your hook)
  const { displayName, email, instagram_handle, hasContact, loading: profileLoading } = useProducerProfile() as any

  const resolveSrc = (input: File | string | null): string => {
    if (!input) return ''
    if (typeof input === 'string') return input.startsWith('data:') || input.startsWith('http') ? input : ''
    return URL.createObjectURL(input)
  }

  useEffect(() => {
    const url = resolveSrc(data.audio)
    setAudioSrc(url)
    return () => {
      if (data.audio instanceof File) URL.revokeObjectURL(url)
    }
  }, [data.audio])

  useEffect(() => {
    const url = resolveSrc(data.video)
    setVideoSrc(url)
    return () => {
      if (data.video instanceof File) URL.revokeObjectURL(url)
    }
  }, [data.video])

  useEffect(() => {
    const image = uploadMode === 'audio-thumbnail' ? data.customThumbnail : data.customThumbnail
    const url = resolveSrc(image)
    setImageSrc(url)
    return () => {
      if (typeof image === 'object' && image instanceof File) {
        URL.revokeObjectURL(url)
      }
    }
  }, [data.customThumbnail, uploadMode])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target
    setData((prev) => ({ ...prev, [name]: value }))
  }

  const validate = () => {
    const err: any = {}
    if (!data.title?.trim()) err.title = 'Title required'
    if (!data.description?.trim()) err.description = 'Description required'
    if (
      !(
        (uploadMode === 'audio-thumbnail' && data.audio && data.customThumbnail) ||
        (uploadMode === 'video' && data.video)
      )
    ) {
      err.media = 'Upload media first'
    }
    setErrors(err)
    return Object.keys(err).length === 0
  }

  const handleImageUpload = async (file: File) => {
    try {
      const aspectRatio = await getImageAspectRatio(file)
      const isRoughly16x9 = Math.abs(aspectRatio - 16 / 9) < 0.2
      if (!isRoughly16x9) {
        setAspectRatioWarning('This image is not 16:9. YouTube may add black bars.')
      } else {
        setAspectRatioWarning(null)
      }
      const url = await uploadToCloudinary(file)
      setData((p: any) => ({
        ...p,
        image: null,
        customThumbnail: url,
      }))
    } catch (err) {
      console.error('❌ Cloudinary image upload failed', err)
      setAspectRatioWarning(null)
    }
  }

  /* ===========================
     Build contact block
     =========================== */
const buildContactBlock = (override?: { email?: string | null; instagram_handle?: string | null }) => {
  const e = override?.email ?? email
  const igUrl = instagramUrlFrom(override?.instagram_handle ?? instagram_handle)
  const lines: string[] = []
  if (e) lines.push(`Email: ${e}`)
  if (igUrl) lines.push(`Instagram | ${igUrl}`)
  return lines.length ? `${HEADER_CONTACT}\n${lines.join('\n')}` : ''
}
  /* ===========================
     Toggle Contact & Links
     =========================== */
  // Update the toggleContactAndLinks function (around line 120)
const toggleContactAndLinks = () => {
  const current = data.description || ''

  // avoid gating while profile is loading (prevents false prompts)
  if (profileLoading) return

  // Prevent opening modal if already open
  if (showOnboarding) return

  // If block present -> remove entire block (header + lines)
  if (hasBlock(current, HEADER_CONTACT)) {
    const cleaned = removeContactBlock(current)
    setData(prev => ({ ...prev, description: cleaned }))
    setActiveInserts(prev => { const s = new Set(prev); s.delete('contact-links'); return s })
    return
  }

  // Use local override OR actual profile state for gating
  const contactReady = localContactReady || (hasContact && !profileLoading)
  
  // Gate only if contact info is truly missing
  if (!contactReady) {
    setForceStep(2)
    setRequireContactOnThisOpen(true)  // enforce only for this open
    setShowOnboarding(true)
    return
  }

  // Insert once (guarded by header check)
  const block = buildContactBlock()
  if (block && !hasBlock(current, HEADER_CONTACT)) {
    const next = appendBlock(current, block)
    setData(prev => ({ ...prev, description: next }))
    setActiveInserts(prev => new Set([...prev, 'contact-links']))
  }
}

  /* ===========================
     License toggle
     =========================== */
  const toggleLicense = (type: 'nonprofit' | 'commercial') => {
    const producer = displayName || 'the producer'
    const licenseTexts = {
      nonprofit: `💰 This beat is free for non-profit use. Credit must be given to (prod.${producer}).`,
      commercial: `💰 This beat is for commercial use. A lease must be purchased. Credit must be given to (prod.${producer}).`,
    }

    const current = data.description || ''

    if (selectedLicense === type) {
      const cleaned = current.replace(licenseTexts[type], '').replace(/\n\n\n+/g, '\n\n').trim()
      setData(prev => ({ ...prev, description: cleaned }))
      setSelectedLicense(null)
    } else {
      let cleaned = current
      if (selectedLicense) {
        cleaned = cleaned
          .replace(licenseTexts.nonprofit, '')
          .replace(licenseTexts.commercial, '')
          .replace(/\n\n\n+/g, '\n\n')
          .trim()
      }
      const newDescription = cleaned ? `${cleaned}\n\n${licenseTexts[type]}` : licenseTexts[type]
      setData(prev => ({ ...prev, description: newDescription }))
      setSelectedLicense(type)
    }
  }

  const getLicenseButtonText = () => {
    if (selectedLicense === 'nonprofit') return 'Free for Non-Profit'
    if (selectedLicense === 'commercial') return 'Commercial Use'
    return 'Add Licensing'
  }

  return (
    <div className="flex flex-col h-full gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">Add post details</h2>

        {/* 🔧 Test Onboarding (optional; remove if not needed) */}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            setForceStep(undefined)
            setRequireContactOnThisOpen(false)
            setShowOnboarding(true)
          }}
        >
          Test Onboarding
        </Button>
      </div>

      <div className="flex gap-2">
        <Button
          variant={uploadMode === 'audio-thumbnail' ? 'default' : 'ghost'}
          onClick={() => {
            setUploadMode('audio-thumbnail')
            setData((p: any) => ({ ...p, video: null, customThumbnail: null }))
          }}
        >
          Upload Audio + Thumbnail
          <AudioLines/>
        </Button>
        <Button
          variant={uploadMode === 'video' ? 'default' : 'ghost'}
          onClick={() => {
            setUploadMode('video')
            setData((p: any) => ({ ...p, audio: null, image: null }))
          }}
        >
          Upload Video
          <Clapperboard/>
        </Button>
      </div>

      {/* main two-column: form (left) | preview (right) */}
      <div className="grid md:grid-cols-2 gap-6 flex-1 overflow-hidden">
        {/* left column */}
        <div className="flex flex-col gap-4 min-h-0 overflow-y-auto p-2">
          {uploadMode === 'audio-thumbnail' ? (
            <>
              {/* side-by-side audio + thumbnail on md+ */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FileUploadCard
                  label="Audio File"
                  fileName={data.audio?.name}
                  onUpload={(file) => setData((p: any) => ({ ...p, audio: file }))}
                  accept="audio/mp3"
                  validated={!!data.audio}
                  enableDropzone={true}
                />
                <FileUploadCard
                  label="Thumbnail Image"
                  fileName={typeof data.customThumbnail === 'string' ? undefined : data.image?.name}
                  onUpload={handleImageUpload}
                  accept="image/*"
                  validated={!!data.customThumbnail}
                  enableDropzone={true}
                />
              </div>
              {aspectRatioWarning && (
                <p className="text-yellow-500 text-sm">{aspectRatioWarning}</p>
              )}
            </>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FileUploadCard
                label="Video File"
                fileName={data.video?.name}
                onUpload={(file) => setData((p: any) => ({ ...p, video: file }))}
                accept="video/mp4"
                validated={!!data.video}
                enableDropzone={true}
                className="md:col-span-2"
              />
            </div>
          )}

          <Label htmlFor="title">Beat Title</Label>
          <Input
            id="title"
            name="title"
            value={data.title}
            onChange={handleChange}
            className="bg-card"
          />
          {errors.title && <p className="text-red-500 text-sm">{errors.title}</p>}

          <Label htmlFor="description">Description</Label>

          {/* Quick Insert Buttons */}
          <div className="flex flex-wrap gap-2 items-center mb-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="secondary"
                  size="sm"
                  className={`flex items-center gap-2 bg-tooltip transition-none ${
                    selectedLicense ? 'border-green-500 border-2' : ''
                  }`}
                >
                  <DollarSign className="w-4 h-4" />
                  {getLicenseButtonText()}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="text-yellow-400 ml-1">
                        <HelpCircle className="w-4 h-4" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      Add standard usage terms for this beat
                    </TooltipContent>
                  </Tooltip>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  onClick={() => toggleLicense('nonprofit')}
                  className={selectedLicense === 'nonprofit' ? 'bg-green-50 text-green-700' : ''}
                >
                  <div className="flex items-center justify-between w-full">
                    Free for Non-Profit
                    {selectedLicense === 'nonprofit' && (
                      <div className="w-2 h-2 rounded-full ml-2 bg-green-500"></div>
                    )}
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => toggleLicense('commercial')}
                  className={selectedLicense === 'commercial' ? 'bg-green-50 text-green-700' : ''}
                >
                  <div className="flex items-center justify-between w-full">
                    Commercial Use
                    {selectedLicense === 'commercial' && (
                      <div className="w-2 h-2 rounded-full ml-2 bg-green-500"></div>
                    )}
                  </div>
                </DropdownMenuItem>
                {selectedLicense && (
                  <>
                    <div className="border-t my-1"></div>
                    <DropdownMenuItem
                      onClick={() => toggleLicense(selectedLicense)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      Remove License
                    </DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

<Button
              variant="secondary"
              size="sm"
              onClick={toggleContactAndLinks}
              disabled={profileLoading || showOnboarding}
              className={`flex items-center gap-2 bg-tooltip transition-none ${
                activeInserts.has('contact-links') ? 'border-green-500 border-2' : ''
              }`}
            >
              <Mail className="w-4 h-4" />
              Contact & Links
            </Button>

            <Button
              variant="secondary"
              size="sm"
              className="flex items-center gap-2 bg-tooltip transition-none"
              onClick={() => setIsHashtagModalOpen(true)}
            >
              <Hash className="w-4 h-4" />
              Add Tags
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-yellow-400 ml-1">
                    <HelpCircle className="w-4 h-4" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Insert saved hashtags into your description
                </TooltipContent>
              </Tooltip>
            </Button>

            <Button
              variant="secondary"
              size="sm"
              className="flex items-center gap-2 bg-tooltip transition-none"
              onClick={() => setIsTemplateModalOpen(true)}
            >
              <FileText className="w-4 h-4" />
              Templates
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-yellow-400 ml-1">
                    <HelpCircle className="w-4 h-4" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  Insert or reuse a saved template
                </TooltipContent>
              </Tooltip>
            </Button>
          </div>

          {/* Taller description now that uploads are side-by-side */}
          <Textarea
            id="description"
            name="description"
            value={data.description}
            onChange={handleChange}
            className={`w-full h-64  text-sm transition-none resize-none ${
              (activeInserts.size > 0 || selectedLicense)
                ? 'bg-muted/30'
                : 'bg-card'
            }`}
          />
          {errors.description && <p className="text-red-500 text-sm">{errors.description}</p>}
        </div>

        {/* right column */}
        <div className="sticky top-0 overflow-y-auto">
          <MediaPreviewCard
            audioSrc={audioSrc}
            videoSrc={videoSrc}
            imageSrc={imageSrc}
            title={data.title}
            description={data.description}
          />
        </div>
      </div>

      <div className="flex justify-between mt-4">
        {!isEditingDraft ? (
          <Button variant="outline" onClick={onPrevious}>Previous</Button>
        ) : <div />}
        <Button onClick={() => validate() && onNext()}>Next</Button>
      </div>

      <HashtagModal
        open={isHashtagModalOpen}
        onOpenChange={setIsHashtagModalOpen}
        onInsert={(tag) => {
          const current = data.description || ''
          if (current.includes('🏷️')) {
            setData((prev: any) => ({
              ...prev,
              description: prev.description + ' ' + tag,
            }))
          } else {
            const newDescription = current ? `${current}\n\n🏷️ ${tag}` : `🏷️ ${tag}`
            setData((prev: any) => ({
              ...prev,
              description: newDescription,
            }))
          }
        }}
      />

    <DescriptionTemplateModal
  open={isTemplateModalOpen}
  onOpenChange={setIsTemplateModalOpen}
  currentDescription={data.description || ''}
  setDescription={(desc) =>
    setData((prev: any) => ({
      ...prev,
      description: desc,
    }))
  }
  hasExistingContent={!!(data.description && data.description.trim())} // Add this line
/>

      {/* Onboarding modal: force Step 2 only when needed for this feature */}
      <OnboardingModal
        open={showOnboarding}
        onClose={() => setShowOnboarding(false)}
        forceStep={forceStep}
        requireContactOnThisOpen={requireContactOnThisOpen}
   onContactSaved={({ email: savedEmail, instagram_handle: savedIg }) => {
  // Set local override immediately
  setLocalContactReady(true)
  
  // Insert immediately (header guard prevents duplicates)
  const current = data.description || ''
          if (!hasBlock(current, HEADER_CONTACT)) {
            const igUrl = savedIg ? `https://instagram.com/${String(savedIg).replace(/^@/, '')}` : ''
            const lines = [
              savedEmail && `Email: ${savedEmail}`,
              igUrl && `IG: ${igUrl}`,
            ].filter(Boolean) as string[]
            if (lines.length) {
              const block = `${HEADER_CONTACT}\n${lines.join('\n')}`
              const next = appendBlock(current, block)
              setData(prev => ({ ...prev, description: next }))
              setActiveInserts(prev => new Set([...prev, 'contact-links']))
            }
          }
          setRequireContactOnThisOpen(false)
          setForceStep(undefined)
          setShowOnboarding(false)
        }}
      />
    </div>
  )
}
